import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { UserDetail } from "./UserDetail";
import { activLabel, exact, invLabel, relTime, roleLabel, shortDate } from "./usersFormat";

const ORDERS = [["ultima_entrada", "Última entrada"], ["minutos_7d", "Minutos 7 días"], ["registro", "Registro"], ["contactos", "Contactos"]];
const COLS = ["Usuario", "Rol", "Invitado por", "Registro", "Última entrada", "Min hoy", "Min 7d", "Prom/día", "Invit. env/reg/act", "Contactos", "Activado", "Fundador"];

const LastSeen = ({ iso, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <button type="button" data-testid={testid} title={exact(iso)} className="underline decoration-dotted text-left"
      onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>{open ? exact(iso) : relTime(iso)}</button>
  );
};

const UserCard = ({ u, onOpen }) => (
  <div role="button" tabIndex={0} data-testid={`user-card-${u.usuario}`} onClick={onOpen} onKeyDown={(e) => e.key === "Enter" && onOpen()}
    className="c-card p-3.5 w-full text-left space-y-1.5 cursor-pointer">
    <div className="flex justify-between gap-2">
      <div className="min-w-0"><p className="font-semibold truncate">{u.nombre_publico}</p><p className="text-[11px] c-muted">@{u.usuario} · {roleLabel(u)}</p></div>
      <span className="text-[11px] shrink-0"><LastSeen iso={u.ultima_entrada} testid={`user-card-last-${u.usuario}`} /></span>
    </div>
    <div className="grid grid-cols-3 gap-1 text-[11px] c-text-2">
      <span>Hoy <b>{u.minutos_hoy}</b> min</span><span>7d <b>{u.minutos_7d}</b> min</span><span>Prom <b>{u.promedio_diario_7d}</b></span>
      <span>Contactos <b>{u.contactos}</b></span><span>Compart. <b>{u.compartibles}</b></span><span>Activado <b>{activLabel(u)}</b></span>
      <span className="col-span-2">Invit. {invLabel(u.invitaciones)}</span><span>{u.numero_fundador ? `Fundador #${u.numero_fundador}` : ""}</span>
    </div>
    <p className="text-[10px] c-muted">Registro {shortDate(u.registro)}{u.invitado_por ? ` · invitado por ${u.invitado_por}` : ""}</p>
  </div>
);

const UserTable = ({ items, onOpen }) => (
  <div className="hidden md:block c-card overflow-x-auto">
    <table data-testid="admin-users-table" className="w-full text-xs">
      <thead><tr className="text-left c-muted">{COLS.map((c) => <th key={c} className="px-2.5 py-2 font-medium whitespace-nowrap">{c}</th>)}</tr></thead>
      <tbody>{items.map((u) => (
        <tr key={u.id} data-testid={`user-row-${u.usuario}`} onClick={() => onOpen(u.id)} className="border-t border-[var(--c-line)] cursor-pointer hover:bg-black/[0.03] transition-colors">
          <td className="px-2.5 py-2"><p className="font-semibold">{u.nombre_publico}</p><p className="c-muted">@{u.usuario}</p></td>
          <td className="px-2.5 py-2">{roleLabel(u)}</td><td className="px-2.5 py-2">{u.invitado_por || "—"}</td>
          <td className="px-2.5 py-2 whitespace-nowrap">{shortDate(u.registro)}</td>
          <td className="px-2.5 py-2 whitespace-nowrap"><LastSeen iso={u.ultima_entrada} testid={`user-row-last-${u.usuario}`} /></td>
          <td className="px-2.5 py-2">{u.minutos_hoy}</td><td className="px-2.5 py-2">{u.minutos_7d}</td><td className="px-2.5 py-2">{u.promedio_diario_7d}</td>
          <td className="px-2.5 py-2">{invLabel(u.invitaciones)}</td><td className="px-2.5 py-2">{u.contactos} · {u.compartibles}</td>
          <td className="px-2.5 py-2">{activLabel(u)}</td><td className="px-2.5 py-2">{u.numero_fundador ? `#${u.numero_fundador}` : "—"}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

export const UsersTab = () => {
  const [q, setQ] = useState("");
  const [orden, setOrden] = useState("ultima_entrada");
  const [pruebas, setPruebas] = useState(false);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/uso/usuarios", { params: { q, orden, pruebas } }).then((r) => { setItems(r.data.items); setErr(null); }).catch((e) => setErr(errMsg(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, orden, pruebas]);
  return (
    <div data-testid="admin-users-tab" className="space-y-3">
      <div className="flex flex-col md:flex-row gap-2">
        <label className="c-card flex items-center gap-2 px-3 py-2 flex-1"><Search className="w-4 h-4 c-muted" />
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o usuario" className="bg-transparent outline-none text-sm flex-1" /></label>
        <select data-testid="admin-users-order" value={orden} onChange={(e) => setOrden(e.target.value)} className="c-card px-3 py-2 text-sm">
          {ORDERS.map(([v, l]) => <option key={v} value={v}>Ordenar: {l}</option>)}</select>
        <label className="flex items-center gap-2 text-xs c-text-2 px-1"><Switch data-testid="admin-users-show-tests" checked={pruebas} onCheckedChange={setPruebas} />Mostrar cuentas de prueba</label>
      </div>
      {err ? <p className="c-card p-4 text-sm text-red-600">{err}</p> : !items ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>
        : items.length === 0 ? <p data-testid="admin-users-empty" className="c-card p-4 text-sm c-text-2">Sin usuarios.</p> : (
          <>
            <p data-testid="admin-users-count" className="text-[11px] c-muted">{items.length} usuarios</p>
            <div className="md:hidden space-y-2">{items.map((u) => <UserCard key={u.id} u={u} onOpen={() => setOpen(u.id)} />)}</div>
            <UserTable items={items} onOpen={setOpen} />
          </>)}
      {open && <UserDetail id={open} onClose={() => setOpen(null)} />}
    </div>
  );
};
